import { useEffect } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useSelector } from "react-redux";

import CrmEnrichmentJobFilters from "app/pages/sales-intelligence/modules/crm-enrichment/components/filters/CrmEnrichmentJobFilters";
import { EnrichmentSettingsBreadcrumbs } from "app/pages/sales-intelligence/modules/crm-enrichment/components/EnrichmentSettingsBreadcrumbs/EnrichmentSettingsBreadcrumbs";
import JobType from "app/pages/sales-intelligence/modules/crm-enrichment/components/settings/CrmEnrichmentJobType/JobType";
import JobEditor from "app/pages/sales-intelligence/modules/crm-enrichment/components/settings/JobEditor/JobEditor";
import CrmEnrichmentFieldsMappingWizardStep from "app/pages/sales-intelligence/modules/crm-enrichment/components/wizard/CrmEnrichmentFieldsMappingWizardStep";
import CrmEnrichmentJobSettings from "app/pages/sales-intelligence/modules/crm-enrichment/components/wizard/CrmEnrichmentJobSettings";
import { useCrmEnrichmentContext } from "app/pages/sales-intelligence/modules/crm-enrichment/contexts/CrmEnrichmentContext";
import useJobRemoveModal from "app/pages/sales-intelligence/modules/crm-enrichment/hooks/useJobRemoveModal";
import crmEnrichmentTrackingService from "app/pages/sales-intelligence/modules/crm-enrichment/services/tracking/trackingService";
import type {
    CrmEnrichmentJob,
    CrmEnrichmentJobObject,
    CrmEnrichmentJobRefreshPeriod,
    CrmEnrichmentJobTabName,
    LeadScoreRefreshDataInterval,
} from "app/pages/sales-intelligence/modules/crm-enrichment/types/types";
import { getDefaultMappings } from "app/pages/sales-intelligence/modules/crm-enrichment/utils/defaultMappings";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import {
    selectHsAccountsColumns,
    selectHsContactsColumns,
    selectHsSettings,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspot/exportSelectors";
import type { ICountryObject } from "app/services/CountryService";

import { HS_OBJECTS_OPTIONS } from "../../constants";
import useCrmEnrichmentJob from "../../hooks/useCrmEnrichmentJob";
import useCrmEnrichmentJobEditorValidation from "../../hooks/useCrmEnrichmentJobEditorValidation";
import useHsEnrichmentJobEditMutation from "../../mutations/useHsEnrichmentJobEditMutation";
import useHsEnrichmentJobQuery from "../../queries/useHsEnrichmentJobQuery";
import CrmEnrichmentJobRemoveModalContainer from "../CrmEnrichmentJobRemoveModalContainer/CrmEnrichmentJobRemoveModalContainer";

const CrmEnrichmentJobEditor = () => {
    const { params } = useCurrentStateAndParams();

    const { crmProvider, i18nKeys } = useCrmEnrichmentContext();

    const { data: job, isError: isQueryError } = useHsEnrichmentJobQuery(params.id);

    const { openModal } = useJobRemoveModal();

    const { mutate: mutateEdit, isPending: isLoading, isSuccess: isMutationSuccess } = useHsEnrichmentJobEditMutation();

    const {
        createJobDTO,
        jobName,
        setJobName,
        selectedJobType,
        selectedJobObject,
        setSelectedJobObject,
        selectedJobRefreshPeriod,
        setSelectedJobRefreshPeriod,
        selectedEnrichmentSource,
        setSelectedEnrichmentSource,
        selectedLeadScoreRefreshDataInterval,
        setSelectedLeadScoreRefreshDataInterval,
        selectedCountry,
        setSelectedCountry,
        onApplyFiltersChange,
        filters,
        onAddFilter,
        onDeleteFilter,
        onFilterChange,
        onCrmFieldFilterChange,
        onResetFilters,
        setFiltersFromInitial,
        crmFieldOptions,
        isApplyFiltersSelected,
        mappings,
        setMappings,
        addMapping,
        removeMapping,
        updateMapping,
    } = useCrmEnrichmentJob(job);

    const hsSettings = useSelector(selectHsSettings);
    const accountsColumns = useSelector(selectHsAccountsColumns);
    const contactsColumns = useSelector(selectHsContactsColumns);
    const mappingColumns = selectedJobObject === "company" ? accountsColumns : contactsColumns;

    const getHsExportSettings = (object: CrmEnrichmentJobObject) =>
        object === "company" ? hsSettings.accounts : hsSettings.contacts;

    const { isNextButtonDisabled } = useCrmEnrichmentJobEditorValidation({
        jobName,
        isApplyFiltersSelected,
        filters,
        selectedJobObject,
        selectedCountry,
        selectedJobRefreshPeriod,
        selectedLeadScoreRefreshDataInterval,
        initialJob: job,
        mappings,
    });

    const goToManagePage = () => {
        void siNavigator.goToCrmManagementPage({ crm: crmProvider });
    };

    useEffect(() => {
        if (isQueryError || isMutationSuccess) {
            goToManagePage();
        }
    }, [isQueryError, isMutationSuccess]);

    const handleSaveJob = () => {
        if (job?.name !== jobName) {
            crmEnrichmentTrackingService.edit.trackName(jobName, crmProvider, job?.name);
        }

        mutateEdit(createJobDTO() as CrmEnrichmentJob);
    };

    const handleUpdateJobObject = (object: CrmEnrichmentJobObject) => {
        setSelectedJobObject(object);

        if (object === job?.enrichmentObject) {
            setFiltersFromInitial();
            setMappings(job?.mappings ?? getDefaultMappings("hubspot", object, getHsExportSettings(object)));
        } else {
            onResetFilters();
            setMappings(getDefaultMappings("hubspot", object, getHsExportSettings(object)));
        }
    };

    const handleTabClick = (tabName: CrmEnrichmentJobTabName) => {
        crmEnrichmentTrackingService.wizard.trackSelectSection(tabName, crmProvider, job?.type);
    };

    const handleSelectRefreshPeriod = (period: CrmEnrichmentJobRefreshPeriod) => {
        crmEnrichmentTrackingService.wizard.trackSelectRefreshInterval(period, crmProvider);

        setSelectedJobRefreshPeriod(period);
    };

    const handleSelectLeadScoreRefreshDataInterval = (interval: LeadScoreRefreshDataInterval | "") => {
        if (interval) {
            crmEnrichmentTrackingService.wizard.trackSelectLeadScoreRefreshInterval(interval, crmProvider);
        }

        setSelectedLeadScoreRefreshDataInterval(interval);
    };

    const handleSelectCountry = (country: ICountryObject) => {
        crmEnrichmentTrackingService.wizard.trackSelectCountry(country.text, crmProvider);

        setSelectedCountry(country);
    };

    if (!job) {
        return null;
    }

    return (
        <>
            <EnrichmentSettingsBreadcrumbs
                crmProvider={crmProvider}
                dataAutomation="hs-enrichment-job-edit-breadcrumbs"
            />
            <JobEditor
                isSaveButtonDisabled={isNextButtonDisabled}
                isLoading={isLoading}
                onSave={handleSaveJob}
                onCancel={goToManagePage}
                onDeleteClick={openModal}
                onTabClick={handleTabClick}
                content={{
                    type: (
                        <JobType
                            jobName={jobName}
                            onSetJobName={setJobName}
                            type={job.type}
                            enrichmentSource={job.enrichmentSource}
                        />
                    ),
                    settings: (
                        <CrmEnrichmentJobSettings
                            translationKeys={i18nKeys}
                            selectedJobType={job.type}
                            initialJobObject={job.enrichmentObject}
                            selectedJobObject={selectedJobObject}
                            selectedJobRefreshPeriod={selectedJobRefreshPeriod}
                            selectedLeadScoreRefreshDataInterval={selectedLeadScoreRefreshDataInterval}
                            selectedEnrichmentSource={selectedEnrichmentSource}
                            onSelectEnrichmentSource={setSelectedEnrichmentSource}
                            selectedCountry={selectedCountry}
                            jobObjectOptions={HS_OBJECTS_OPTIONS}
                            showEnrichmentLevel={false}
                            onSelectJobRefreshPeriod={handleSelectRefreshPeriod}
                            onSelectLeadScoreRefreshDataInterval={handleSelectLeadScoreRefreshDataInterval}
                            onSelectJobObject={handleUpdateJobObject}
                            onSelectCountry={handleSelectCountry}
                        />
                    ),
                    filters: (
                        <CrmEnrichmentJobFilters
                            translationKeys={i18nKeys}
                            isApplyFiltersSelected={isApplyFiltersSelected}
                            onApplyFiltersChange={onApplyFiltersChange}
                            filters={filters}
                            onAddFilter={onAddFilter}
                            onDeleteFilter={onDeleteFilter}
                            onFilterChange={onFilterChange}
                            onCrmFieldFilterChange={onCrmFieldFilterChange}
                            crmFieldOptions={crmFieldOptions}
                            selectedJobObject={selectedJobObject}
                        />
                    ),
                    "fields-mapping": (
                        <CrmEnrichmentFieldsMappingWizardStep
                            crmProvider="hubspot"
                            enrichmentObject={selectedJobObject}
                            jobType={selectedJobType}
                            mappings={mappings}
                            columns={mappingColumns}
                            isColumnsLoading={false}
                            onAddMapping={addMapping}
                            onRemoveMapping={removeMapping}
                            onUpdateMapping={updateMapping}
                            showHeader={false}
                        />
                    ),
                }}
            />

            <CrmEnrichmentJobRemoveModalContainer
                job={job}
                onSuccess={goToManagePage}
            />
        </>
    );
};

export default CrmEnrichmentJobEditor;
